import { useState, useEffect, useCallback } from 'react';
import { getActivity, ActivityEvent, formatRelativeTime } from '../utils/api';

function formatProviderHint(hint: string | null): string {
  if (!hint) return 'Unknown';

  const providers: Record<string, string> = {
    'gmail_image_proxy': 'Gmail',
    'outlook_proxy': 'Outlook',
    'yahoo_proxy': 'Yahoo',
    'apple_proxy': 'Apple Mail',
  };

  return providers[hint] || hint;
}

function formatLocation(city: string | null, country: string | null): string {
  if (city && country) return `${city}, ${country}`;
  if (country) return country;
  return 'Unknown';
}

type DeviceType = 'mobile' | 'tablet' | 'desktop';

function parseDeviceType(ua: string | null): DeviceType {
  if (!ua) return 'desktop';
  const lower = ua.toLowerCase();
  if (lower.includes('iphone') || lower.includes('android') && lower.includes('mobile')) {
    return 'mobile';
  }
  if (lower.includes('ipad') || lower.includes('tablet') || (lower.includes('android') && !lower.includes('mobile'))) {
    return 'tablet';
  }
  return 'desktop';
}

function DeviceIcon({ type }: { type: DeviceType }) {
  if (type === 'mobile') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    );
  }
  if (type === 'tablet') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
        <line x1="12" y1="18" x2="12.01" y2="18" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

interface ActivityFeedProps {
  onNewEvent?: (event: ActivityEvent, isLiveUpdate: boolean) => void;
  since?: string;
  onSelectEmail?: (emailId: string) => void;
}

export function ActivityFeed({ onNewEvent, since, onSelectEmail }: ActivityFeedProps) {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    try {
      const data = await getActivity(since);
      setEvents(data);
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    } finally {
      setLoading(false);
    }
  }, [since]);

  useEffect(() => {
    fetchEvents();
    const interval = setInterval(fetchEvents, 10000);
    return () => clearInterval(interval);
  }, [fetchEvents]);

  // Handle new events from parent (real-time)
  useEffect(() => {
    if (onNewEvent) {
      // This effect just registers for callbacks
    }
  }, [onNewEvent]);

  const addEvent = useCallback((event: ActivityEvent) => {
    setEvents((prev) => {
      // Avoid duplicates
      if (prev.some((e) => e.id === event.id)) return prev;
      return [event, ...prev].slice(0, 100);
    });
  }, []);

  // Expose addEvent for parent component
  useEffect(() => {
    if (onNewEvent) {
      (window as any).__addActivityEvent = addEvent;
    }
    return () => {
      delete (window as any).__addActivityEvent;
    };
  }, [addEvent, onNewEvent]);

  if (loading) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="empty-state">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2>Activity Feed</h2>
      </div>
      <div className="card-body" style={{ padding: 0 }}>
        {events.length === 0 ? (
          <div className="empty-state">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            <p>No tracked emails yet</p>
          </div>
        ) : (
          <table className="table table-clickable">
            <thead>
              <tr>
                <th>Time</th>
                <th>To</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Location</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => {
                const deviceType = parseDeviceType(event.raw_ua);
                const isOpened = event.open_count > 0;
                return (
                  <tr
                    key={event.id}
                    onClick={() => onSelectEmail?.(event.email_id)}
                  >
                    <td className="relative-time">
                      {formatRelativeTime(event.created_at)}
                    </td>
                    <td>{event.to_email || '—'}</td>
                    <td>{event.subject || 'No subject'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {isOpened ? (
                          <span className="badge badge-success">
                            {event.open_count} open{event.open_count !== 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span className="badge badge-secondary">
                            Sent
                          </span>
                        )}
                        {event.click_count > 0 && (
                          <span className="badge badge-warning" style={{ background: '#ffc107', color: '#000' }}>
                            {event.click_count} click{event.click_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="location-cell">
                        {isOpened ? formatLocation(event.city, event.country) : '—'}
                      </span>
                    </td>
                    <td>
                      <span className="source-cell">
                        {isOpened && <DeviceIcon type={deviceType} />}
                        <span className="badge badge-info">
                          {isOpened ? formatProviderHint(event.provider_hint) : event.source === 'gmail_extension' ? 'Gmail' : 'Dashboard'}
                        </span>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
